import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { ConfirmationService, Dialog, SelectItem } from 'primeng';
import { SettingsService } from '../../../services';
import { SupplierInboxJobsService } from '../../../services';
import { ITaskInfo } from '../../../interfaces/task-info';
import { IDriver } from '../../../interfaces/driver';
import {
  DialogStep,
  DialogStepPosition,
} from '../../../components/task-supplier-schedule/supplier-schedule-dialog.component';
import { TaskSupplierScheduleSource } from '../../../components/task-supplier-schedule/task-supplier-schedule-store';
import { UserService } from '../../../services';

@Component({
  selector: 'allocate-driver-dialog',
  templateUrl: './allocate-driver-dialog.component.html',
  styleUrls: ['./allocate-driver-dialog.component.scss'],
})
export class AllocateDriverDialogComponent {
  @ViewChild('dialog') dialog!: Dialog;

  @Input() jobId: number;

  @Input() visible: boolean = false;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() jobAllocated: EventEmitter<number> = new EventEmitter<number>();

  noDriverTasks: ITaskInfo[] = [];

  supplierDrivers: IDriver[] = [];

  public selectedDriverId: number;

  public selectedTask: ITaskInfo = null;

  _taskSelectTemplate: TemplateRef<any> = null;

  @ViewChild('taskSelectTemplate') set taskSelectTemplateContent(content: TemplateRef<any>) {
    this._taskSelectTemplate = content;
    this.setSteps();
  }

  setSteps() {
    this.steps = [
      {
        header: 'Allocate Driver',
        template: this._taskSelectTemplate,
        dialogClass: '',
        position: DialogStepPosition.Before,
        disabled: this.selectedDriverId == null || this.selectedTask == null,
      },
    ];
  }

  public steps: DialogStep[] = [];

  public driverSelectItems: SelectItem[];

  public get tasks(): ITaskInfo[] {
    return this.selectedTask ? [this.selectedTask] : [];
  }

  public taskScheduleSource: TaskSupplierScheduleSource = TaskSupplierScheduleSource.JobDeployment;

  constructor(
    public userService: UserService,
    public settingsService: SettingsService,
    private supplierInboxJobsService: SupplierInboxJobsService,
    private confirmationService: ConfirmationService,
  ) {}

  public get selectedDriver(): IDriver {
    return this.supplierDrivers.find((x) => x.id === this.selectedDriverId);
  }

  async onShow() {
    this.selectedTask = null;
    this.selectedDriverId = null;

    const res = await this.supplierInboxJobsService.getSupplierUserDriversForJob(this.jobId);

    if (res != null) {
      this.supplierDrivers = res;
      this.driverSelectItems = res
        .filter((x) => !x.isLockedOut)
        .map((d) => ({
          label: d.nickName,
          value: d.id,
        }));
    }

    await this.getNoDriversJobTasks();
  }

  async getNoDriversJobTasks() {
    const res = await this.supplierInboxJobsService.getNoDriversJobTasks(this.jobId);
    this.selectedDriverId = null;
    this.selectedTask = res.length == 1 ? res[0] : null;
    this.noDriverTasks = res;
  }

  onClose() {
    this.visibleChange.emit(false);
  }

  private toDisplayWeightConfirmation(): boolean {
    return this.noDriverTasks?.some((task) => task.vehicle.kerbWeight >= 3500) ?? false;
  }

  async allocateDriver() {
    try {
      if (this.toDisplayWeightConfirmation()) {
        const confirmed = await this.showConfirmationDialog(
          `Are you sure ${this.selectedDriver.firstName} ${this.selectedDriver.surname} has the capability to carry out HGV movements?`,
          'Confirmation',
        );

        if (!confirmed) return;
      }

      await this.supplierInboxJobsService.allocateDriver({
        taskId: this.selectedTask.id,
        driverId: this.selectedDriverId,
        taskSupplierSchedule: null,
      });

      this.jobAllocated.emit(this.jobId);
      this.onClose();
    } catch (error) {
      console.error('Error allocating driver:', error);
    }
  }

  private async showConfirmationDialog(message: string, header: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'allocate-driver',
        message,
        header,
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }
}
